import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useNavigationType, useParams } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Layout } from "./Layout";
import { LoginPage } from "./LoginPage";
import { ForgotPasswordPage } from "./ForgotPasswordPage";
import { ResetPasswordPage } from "./ResetPasswordPage";
import { VerifyEmailPage } from "./VerifyEmailPage";
import { CookieBanner } from "./components/CookieBanner";
import { RequireAuth } from "./RequireAuth";
import { TodayPage } from "./pages/TodayPage";

import { AthletePlanPage } from "./pages/AthletePlanPage";

import { ChatPage } from "./pages/ChatPage";
import { AnalysisCursorProvider } from "./components/analytics/AnalysisCursor";
import { ProfilePage } from "./pages/ProfilePage";

import { ErrorBoundary } from "./components/ErrorBoundary";

import { EventsPage } from "./pages/EventsPage";

import { ToastHost } from "./components/ToastHost";

import { MorePage } from "./pages/MorePage";
import { FormPage } from "./pages/FormPage";

import { useConsentStatus } from "./consentApi";
import { Skeleton, SkeletonCard } from "./components/Skeleton";
// Lazy-loaded: pulls R3F + three + drei + @readyplayerme into a separate
// chunk so /login (and any other route) doesn't pay the ~3 MB bundle cost.
const AvatarPage = lazy(() => import("./pages/AvatarPage").then(m => ({ default: m.AvatarPage })));
// dev/design kitchen-sink — default export, lazy so it never ships in a
// normal-route chunk.
const DesignGalleryPage = lazy(() => import("./pages/DesignGalleryPage"));
const AvatarViewer3D = lazy(() => import("./components/AvatarViewer3D").then(m => ({ default: m.AvatarViewer3D })));

/**
 * ═══ АНАЛИЗ ГРУЗИТСЯ ОТДЕЛЬНЫМ КУСКОМ — 2026-08-04 ═══
 *
 * Замер: главный бандл 1.5 MB gzip ОДНИМ файлом, лениво грузились три
 * страницы из ста тридцати семи. Человек на экране логина качал recharts,
 * тридцать две линзы и весь Анализ целиком — то есть платил за экраны,
 * которых мог не открыть ни разу.
 *
 * Анализ выбран первым не по размеру файла, а по связям: он тянет recharts
 * и весь каталог линз, и это самая тяжёлая связка приложения.
 *
 * Условие, без которого разрез вредит: `Suspense` обязан отдавать заглушку
 * ТОЙ ЖЕ высоты, иначе вернётся скачок раскладки, который только что
 * убрали скелетонами (CLS 0.358 → 0.013).
 */
const AnalyticsEntry = lazy(() => import("./pages/AnalyticsEntry").then(m => ({ default: m.AnalyticsEntry })));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage").then(m => ({ default: m.AnalyticsPage })));
const AnalysisStudioPage = lazy(() => import("./pages/AnalysisStudioPage").then(m => ({ default: m.AnalysisStudioPage })));
const LensHubPage = lazy(() => import("./pages/LensHubPage").then(m => ({ default: m.LensHubPage })));
const LensPage = lazy(() => import("./pages/LensPage").then(m => ({ default: m.LensPage })));


/* ── Разрез бандла: страницы приезжают со своим чанком ──────────────
   Продолжение работы, начатой с Анализа (см. комментарий выше). Замер до:
   главный чанк 1265 кБ gzip — его качает человек на экране логина, платя
   за экраны, которых может не открыть ни разу.

   В главном чанке намеренно остались: пять вкладок нижней навигации
   (Сегодня/План/Форма/Анализ/Ещё) — их открывают сразу, и лишний запрос
   там дороже байтов; а также ChatPage, ProfilePage и EventsPage — их
   тянут другие модули (ChatDrawer, TwoFactorBlock/PushBlock,
   SavedEventsPage), поэтому ленивость их всё равно не вынесет. */
const RecordPage = lazy(() => import("./pages/RecordPage").then(m => ({ default: m.RecordPage })));
const RaceCenterPage = lazy(() => import("./pages/RaceCenterPage").then(m => ({ default: m.RaceCenterPage })));
const RaceResultDetailPage = lazy(() => import("./pages/RaceResultDetailPage").then(m => ({ default: m.RaceResultDetailPage })));
const AthleteSettingsPage = lazy(() => import("./pages/AthleteSettingsPage").then(m => ({ default: m.AthleteSettingsPage })));
const GoalEditPage = lazy(() => import("./pages/GoalEditPage").then(m => ({ default: m.GoalEditPage })));
const GoalInsightsPage = lazy(() => import("./pages/GoalInsightsPage").then(m => ({ default: m.GoalInsightsPage })));
const GoalJourneyPage = lazy(() => import("./pages/GoalJourneyPage").then(m => ({ default: m.GoalJourneyPage })));
const WellnessPage = lazy(() => import("./pages/WellnessPage").then(m => ({ default: m.WellnessPage })));
const WellnessCheckinPage = lazy(() => import("./pages/WellnessCheckinPage").then(m => ({ default: m.WellnessCheckinPage })));
const DiaryPage = lazy(() => import("./pages/DiaryPage").then(m => ({ default: m.DiaryPage })));
const QuestionnairePage = lazy(() => import("./pages/QuestionnairePage").then(m => ({ default: m.QuestionnairePage })));
const OnboardingConnectPage = lazy(() => import("./pages/OnboardingConnectPage").then(m => ({ default: m.OnboardingConnectPage })));
const AchievementsPage = lazy(() => import("./pages/AchievementsPage").then(m => ({ default: m.AchievementsPage })));
const ZonesPage = lazy(() => import("./pages/ZonesPage").then(m => ({ default: m.ZonesPage })));
const InjuryLogPage = lazy(() => import("./pages/InjuryLogPage").then(m => ({ default: m.InjuryLogPage })));
const PlanHistoryPage = lazy(() => import("./pages/PlanHistoryPage").then(m => ({ default: m.PlanHistoryPage })));
const PlanGeneratePage = lazy(() => import("./pages/PlanGeneratePage").then(m => ({ default: m.PlanGeneratePage })));
const RaceCompareSplitsPage = lazy(() => import("./pages/RaceCompareSplitsPage").then(m => ({ default: m.RaceCompareSplitsPage })));
const CycleComparePage = lazy(() => import("./pages/CycleComparePage").then(m => ({ default: m.CycleComparePage })));
const PrByRoutePage = lazy(() => import("./pages/PrByRoutePage").then(m => ({ default: m.PrByRoutePage })));
const PlanTemplatesPage = lazy(() => import("./pages/PlanTemplatesPage").then(m => ({ default: m.PlanTemplatesPage })));
const PlanTemplateDetailPage = lazy(() => import("./pages/PlanTemplateDetailPage").then(m => ({ default: m.PlanTemplateDetailPage })));
const TravelModePage = lazy(() => import("./pages/TravelModePage").then(m => ({ default: m.TravelModePage })));
const StravaSegmentsPage = lazy(() => import("./pages/StravaSegmentsPage").then(m => ({ default: m.StravaSegmentsPage })));
const CarbTimingPage = lazy(() => import("./pages/CarbTimingPage").then(m => ({ default: m.CarbTimingPage })));
const ResiliencePage = lazy(() => import("./pages/ResiliencePage").then(m => ({ default: m.ResiliencePage })));
const OwSpotsPage = lazy(() => import("./pages/OwSpotsPage").then(m => ({ default: m.OwSpotsPage })));
const RouteHeatmapPage = lazy(() => import("./pages/RouteHeatmapPage").then(m => ({ default: m.RouteHeatmapPage })));
const EquipmentPage = lazy(() => import("./pages/EquipmentPage").then(m => ({ default: m.EquipmentPage })));
const KBPage = lazy(() => import("./pages/KBPage").then(m => ({ default: m.KBPage })));
const KBArticlePage = lazy(() => import("./pages/KBArticlePage").then(m => ({ default: m.KBArticlePage })));
const DayPage = lazy(() => import("./pages/DayPage").then(m => ({ default: m.DayPage })));
const WorkoutDetailPage = lazy(() => import("./pages/WorkoutDetailPage").then(m => ({ default: m.WorkoutDetailPage })));
const WorkoutBuilderPage = lazy(() => import("./pages/WorkoutBuilderPage").then(m => ({ default: m.WorkoutBuilderPage })));
const ThresholdTestPage = lazy(() => import("./pages/ThresholdTestPage").then(m => ({ default: m.ThresholdTestPage })));
const SportProfilePage = lazy(() => import("./pages/SportProfilePage").then(m => ({ default: m.SportProfilePage })));
const AchievementDetailPage = lazy(() => import("./pages/AchievementDetailPage").then(m => ({ default: m.AchievementDetailPage })));
const StreaksPage = lazy(() => import("./pages/StreaksPage").then(m => ({ default: m.StreaksPage })));
const BugReportPage = lazy(() => import("./pages/BugReportPage").then(m => ({ default: m.BugReportPage })));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage").then(m => ({ default: m.FeedbackPage })));
const RoadmapPage = lazy(() => import("./pages/RoadmapPage").then(m => ({ default: m.RoadmapPage })));
const WhatsNewPage = lazy(() => import("./pages/WhatsNewPage").then(m => ({ default: m.WhatsNewPage })));
const CalendarSyncPage = lazy(() => import("./pages/CalendarSyncPage").then(m => ({ default: m.CalendarSyncPage })));
const LabPage = lazy(() => import("./pages/LabPage").then(m => ({ default: m.LabPage })));
const BillingPage = lazy(() => import("./pages/settings/BillingPage").then(m => ({ default: m.BillingPage })));
const NotificationsPage = lazy(() => import("./pages/settings/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const LanguagePage = lazy(() => import("./pages/settings/LanguagePage").then(m => ({ default: m.LanguagePage })));
const IntegrationsPage = lazy(() => import("./pages/settings/IntegrationsPage").then(m => ({ default: m.IntegrationsPage })));
const SportMetricsPage = lazy(() => import("./pages/settings/SportMetricsPage").then(m => ({ default: m.SportMetricsPage })));
const EmailChangePage = lazy(() => import("./pages/settings/EmailChangePage").then(m => ({ default: m.EmailChangePage })));
const TwoFactorPage = lazy(() => import("./pages/settings/TwoFactorPage").then(m => ({ default: m.TwoFactorPage })));
const FaqPage = lazy(() => import("./pages/settings/FaqPage").then(m => ({ default: m.FaqPage })));
const DangerZonePage = lazy(() => import("./pages/settings/DangerZonePage").then(m => ({ default: m.DangerZonePage })));
const NotifPermissionPage = lazy(() => import("./pages/NotifPermissionPage").then(m => ({ default: m.NotifPermissionPage })));
const PRDetailPage = lazy(() => import("./pages/PRDetailPage").then(m => ({ default: m.PRDetailPage })));
const MonthInReviewPage = lazy(() => import("./pages/MonthInReviewPage").then(m => ({ default: m.MonthInReviewPage })));
const YearInReviewPage = lazy(() => import("./pages/YearInReviewPage").then(m => ({ default: m.YearInReviewPage })));
const PrivacyDashboardPage = lazy(() => import("./pages/PrivacyDashboardPage").then(m => ({ default: m.PrivacyDashboardPage })));
const WellnessSubPage = lazy(() => import("./pages/WellnessSubPage").then(m => ({ default: m.WellnessSubPage })));
const EquipmentSubPage = lazy(() => import("./pages/EquipmentSubPage").then(m => ({ default: m.EquipmentSubPage })));
const OnboardingHubPage = lazy(() => import("./pages/OnboardingHubPage").then(m => ({ default: m.OnboardingHubPage })));
const EventDetailPage = lazy(() => import("./pages/EventDetailPage").then(m => ({ default: m.EventDetailPage })));
const MyPredictionsPage = lazy(() => import("./pages/MyPredictionsPage").then(m => ({ default: m.MyPredictionsPage })));
const SavedEventsPage = lazy(() => import("./pages/SavedEventsPage").then(m => ({ default: m.SavedEventsPage })));
const PastRacesPage = lazy(() => import("./pages/PastRacesPage").then(m => ({ default: m.PastRacesPage })));
const ReflectionsPage = lazy(() => import("./pages/ReflectionsPage").then(m => ({ default: m.ReflectionsPage })));
const SparringHubPage = lazy(() => import("./pages/SparringHubPage").then(m => ({ default: m.SparringHubPage })));
const ChallengesPage = lazy(() => import("./pages/ChallengesPage").then(m => ({ default: m.ChallengesPage })));
const ShoesPage = lazy(() => import("./pages/ShoesPage").then(m => ({ default: m.ShoesPage })));
const FriendsPage = lazy(() => import("./pages/FriendsPage").then(m => ({ default: m.FriendsPage })));
const RaceTeamsPage = lazy(() => import("./pages/RaceTeamsPage").then(m => ({ default: m.RaceTeamsPage })));
const PairPage = lazy(() => import("./pages/PairPage").then(m => ({ default: m.PairPage })));
const MentalityPage = lazy(() => import("./pages/MentalityPage").then(m => ({ default: m.MentalityPage })));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage").then(m => ({ default: m.MarketplacePage })));
const CoachProfilePage = lazy(() => import("./pages/CoachProfilePage").then(m => ({ default: m.CoachProfilePage })));
const ExercisesPage = lazy(() => import("./pages/ExercisesPage").then(m => ({ default: m.ExercisesPage })));
const ExerciseDetailPage = lazy(() => import("./pages/ExerciseDetailPage").then(m => ({ default: m.ExerciseDetailPage })));
const LandingPage = lazy(() => import("./pages/LandingPage").then(m => ({ default: m.LandingPage })));
const FoundingApplyPage = lazy(() => import("./pages/FoundingApplyPage").then(m => ({ default: m.FoundingApplyPage })));
const GearPage = lazy(() => import("./pages/GearPage").then(m => ({ default: m.GearPage })));
const InboxPage = lazy(() => import("./pages/InboxPage"));
const InviteAcceptPage = lazy(() => import("./pages/InviteAcceptPage"));
const PublicReportPage = lazy(() => import("./pages/PublicReportPage"));
const RaceTeamInvitePage = lazy(() => import("./pages/RaceTeamInvitePage"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const PathAndGoalPage = lazy(() => import("./pages/PathAndGoalPage"));
const QuickLogPage = lazy(() => import("./pages/QuickLogPage"));
const KnowledgePage = lazy(() => import("./pages/KnowledgePage").then(m => ({ default: m.KnowledgePage })));
const KnowledgeArticlePage = lazy(() => import("./pages/KnowledgeArticlePage").then(m => ({ default: m.KnowledgeArticlePage })));
const OnboardingIntroPage = lazy(() => import("./pages/OnboardingIntroPage"));
const OnboardingWizardPage = lazy(() => import("./pages/OnboardingWizardPage").then(m => ({ default: m.OnboardingWizardPage })));
const PaceZoneCalcPage = lazy(() => import("./pages/PaceZoneCalcPage").then(m => ({ default: m.PaceZoneCalcPage })));
const NutritionLogPage = lazy(() => import("./pages/NutritionLogPage").then(m => ({ default: m.NutritionLogPage })));
const BikeFitPage = lazy(() => import("./pages/BikeFitPage").then(m => ({ default: m.BikeFitPage })));
const SponsorPage = lazy(() => import("./pages/SponsorPage").then(m => ({ default: m.SponsorPage })));
const RaceScoutPage = lazy(() => import("./pages/RaceScoutPage").then(m => ({ default: m.RaceScoutPage })));
const PregnancyPage = lazy(() => import("./pages/PregnancyPage").then(m => ({ default: m.PregnancyPage })));
const DisabilityPage = lazy(() => import("./pages/DisabilityPage").then(m => ({ default: m.DisabilityPage })));
const PricingPage = lazy(() => import("./pages/PricingPage").then(m => ({ default: m.PricingPage })));
const TermsPage = lazy(() => import("./pages/TermsPage").then(m => ({ default: m.TermsPage })));
const TermsSoloPage = lazy(() => import("./pages/TermsSoloPage").then(m => ({ default: m.TermsSoloPage })));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage").then(m => ({ default: m.ImpressumPage })));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const DatenschutzPage = lazy(() => import("./pages/DatenschutzPage").then(m => ({ default: m.DatenschutzPage })));
const AgbPage = lazy(() => import("./pages/AgbPage").then(m => ({ default: m.AgbPage })));
const WiderrufPage = lazy(() => import("./pages/WiderrufPage").then(m => ({ default: m.WiderrufPage })));
const ConsentGatePage = lazy(() => import("./pages/ConsentGatePage").then(m => ({ default: m.ConsentGatePage })));
const CrisisDetrainPage = lazy(() => import("./pages/CrisisDetrainPage").then(m => ({ default: m.CrisisDetrainPage })));
const CrisisIllnessPage = lazy(() => import("./pages/CrisisIllnessPage").then(m => ({ default: m.CrisisIllnessPage })));
const SearchPage = lazy(() => import("./pages/SearchPage").then(m => ({ default: m.SearchPage })));
const StrengthLogPage = lazy(() => import("./pages/StrengthLogPage").then(m => ({ default: m.StrengthLogPage })));
const AltitudeProtocolPage = lazy(() => import("./pages/AltitudeProtocolPage").then(m => ({ default: m.AltitudeProtocolPage })));
const RaceWeatherPage = lazy(() => import("./pages/RaceWeatherPage").then(m => ({ default: m.RaceWeatherPage })));
const BrickSessionPage = lazy(() => import("./pages/BrickSessionPage").then(m => ({ default: m.BrickSessionPage })));
const SwimDrillPage = lazy(() => import("./pages/SwimDrillPage").then(m => ({ default: m.SwimDrillPage })));
const HeatAcclimPage = lazy(() => import("./pages/HeatAcclimPage").then(m => ({ default: m.HeatAcclimPage })));
const LiveRacePage = lazy(() => import("./pages/LiveRacePage").then(m => ({ default: m.LiveRacePage })));
const RacePacingPage = lazy(() => import("./pages/RacePacingPage").then(m => ({ default: m.RacePacingPage })));
const RaceFuelingPage = lazy(() => import("./pages/RaceFuelingPage").then(m => ({ default: m.RaceFuelingPage })));
const RaceMorningPage = lazy(() => import("./pages/RaceMorningPage").then(m => ({ default: m.RaceMorningPage })));
const LiveWorkoutPage = lazy(() => import("./pages/LiveWorkoutPage").then(m => ({ default: m.LiveWorkoutPage })));
const DevicesPage = lazy(() => import("./pages/DevicesPage").then(m => ({ default: m.DevicesPage })));

/**
 * Экран-заглушка на время загрузки чанка страницы.
 *
 * Держит вертикаль, чтобы переход не мигал пустотой: карточки-скелетоны
 * той же формы, что у большинства экранов приложения. `aria-busy` — чтобы
 * скринридер сказал «загружается», а не молчал.
 */
function RouteFallback() {
  return (
    <div
      className="px-4 py-6 max-w-md mx-auto space-y-3 sm:max-w-none"
      data-testid="route-fallback"
      role="status"
      aria-busy="true"
    >
      <Skeleton className="h-7 w-40" />
      <SkeletonCard className="h-24" />
      <SkeletonCard className="h-56" />
    </div>
  );
}

function ConsentGateRoute() {
  const { data } = useConsentStatus();
  const loc = useLocation();
  const from = (loc.state as { from?: string } | null)?.from || "/";
  if (!data?.needs_consent) return <Navigate to={from} replace />;
  return <ConsentGatePage pendingTypes={data.pending_types} redirectTo={from} />;
}

function AvatarViewer3DTest() {
  const ZONES = ["FRESH", "OPTIMAL", "BUILD", "LOAD", "OVERREACH", "RECOVERY", "DETRAINING"];
  const [z, setZ] = useState("OPTIMAL");
  return (
    <div style={{ minHeight: "100vh", background: "#09090b", padding: 16, color: "#fff" }}>
      <h2 style={{ fontSize: 14, color: "#a1a1aa", marginBottom: 12 }}>AVATAR TEST (no auth)</h2>
      <pre id="avatar-debug" style={{
        background: "#18181b", color: "#a3e635", padding: 8, fontSize: 10,
        maxHeight: 120, overflow: "auto", marginBottom: 12, fontFamily: "monospace",
      }} />
      <AvatarViewer3D zone={z} height={420} debug />
      <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 6 }}>
        {ZONES.map(zz => (
          <button
            key={zz}
            onClick={() => setZ(zz)}
            style={{
              padding: "6px 12px", fontSize: 11, borderRadius: 8, cursor: "pointer",
              background: z === zz ? "#3f3f46" : "transparent",
              border: `1px solid ${z === zz ? "#a1a1aa" : "#3f3f46"}`,
              color: z === zz ? "#fff" : "#a1a1aa",
            }}
          >
            {zz}
          </button>
        ))}
      </div>
    </div>
  );
}

/** On mount, if URL contains hash route like '#/goal', navigate there.
 * Telegram WebApp drops the path component but preserves hash. */
function HashBouncer() {
  const nav = useNavigate();
  useEffect(() => {
    // Read deep-link target from (in order): hash, ?go=, Telegram start_param
    const h = window.location.hash;
    const sp = new URLSearchParams(window.location.search);
    const goParam = sp.get("go");
    const tg = (window as any).Telegram?.WebApp;
    const startParam: string | undefined = tg?.initDataUnsafe?.start_param;
    let target: string | null = null;
    if (h && h.startsWith("#/") && h.length > 2) target = h.slice(1);
    else if (goParam && goParam.startsWith("/")) target = goParam;
    else if (startParam) {
      // start_param can carry a route like "goal" or "analytics" (encoded without leading slash)
      const decoded = decodeURIComponent(startParam);
      if (decoded.startsWith("/")) target = decoded;
      else target = "/" + decoded;
    }
    // Persist target across login redirect (sessionStorage survives, location.state does not after Navigate)
    if (target && target !== "/") {
      try { sessionStorage.setItem("postLoginRedirect", target); } catch {
        // silent: sessionStorage unavailable (private mode / quota)
      }
    }
    if (target) {
      // Clear hash/query so refresh stays put
      window.history.replaceState(null, "", window.location.pathname);
      nav(target, { replace: true });
    }
  }, []);
  return null;
}

const qc = new QueryClient();

function CanonicalUpdater() {
  // TD-94 — Lighthouse SEO flagged the static canonical that ships in
  // index.html as "stale" once the user navigates inside the SPA.
  // We update it on every route change so search engines see a fresh
  // canonical URL per page.
  const loc = useLocation();
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    if (!link) return;
    const url = new URL(window.location.href);
    // Drop tracking params + hash from canonical — same hygiene as
    // most SEO-aware SPAs (Vercel, Linear).
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]
      .forEach((k) => url.searchParams.delete(k));
    url.hash = "";
    link.href = url.toString();
  }, [loc.pathname, loc.search]);
  return null;
}

function SwRegistrar() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      // updateViaCache:none — sw.js всегда мимо HTTP-кэша (mobile cache fix)
      navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" }).catch(() => {
        // silent: PWA service-worker registration failure is non-blocking
        // (app works as plain SPA without offline cache).
      });
    }
  }, []);
  return null;
}

/**
 * Сброс скролла при смене экрана — 2026-08-04.
 *
 * SPA сохраняет позицию прокрутки между роутами: «Форма» открывалась на
 * «Порогах», «Анализ» — на середине студии, если предыдущий экран был
 * прокручен. Новый pathname = новый экран = вид с начала. Кнопки
 * назад/вперёд браузера не трогаем (POP) — там возврат к месту честнее.
 */
function ScrollResetOnNavigate() {
  const location = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType !== "POP") window.scrollTo(0, 0);
  }, [location.pathname, navType]);
  return null;
}

/** Старый адрес разбора гонки → канонический `/race-results/:resultId`. */
function RaceRecapLegacyRedirect() {
  const { id } = useParams<{ id?: string }>();
  return <Navigate to={id ? `/race-results/${id}` : "/goal/past"} replace />;
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={qc}>
        <BrowserRouter>
          <HashBouncer />
          <CanonicalUpdater />
          <ScrollResetOnNavigate />
          <SwRegistrar />
          <ToastHost />
          <CookieBanner />
          {/*
            Заглушка вместо пустоты — 2026-08-04.
            `fallback={null}` означает белый экран на время загрузки чанка,
            а после разреза Анализа этот путь стал обычным, а не редким.
            Пустота тут — тот же скачок раскладки, что убрали скелетонами.
          */}
          <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/founding" element={<FoundingApplyPage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          {/* T5-4: публичный read-only отчёт по токен-ссылке */}
          <Route path="/r/:token" element={<PublicReportPage />} />
          <Route path="/race-team-invite/:token" element={<RaceTeamInvitePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/terms/solo" element={<TermsSoloPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/agb" element={<AgbPage />} />
          <Route path="/widerruf" element={<WiderrufPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/marketplace/:coachId" element={<CoachProfilePage />} />
          {/* dev-only: avatar viewer test */}
          {import.meta.env.DEV && (
            <Route path="/avatar-test" element={<AvatarViewer3DTest />} />
          )}
          <Route path="/privacy" element={<DatenschutzPage />} />
          {/* C3 — KB публичен: и аноним, и авторизованный видят каталог.
              Авторизованный получает personal-layer внутри страницы. */}
          <Route path="/kb" element={<KBPage />} />
          <Route path="/kb/:slug" element={<KBArticlePage />} />
          <Route
            path="/consent"
            element={
              <RequireAuth>
                <ConsentGateRoute />
              </RequireAuth>
            }
          />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Layout>
                  <Routes>
                    {/* P1.6b route swap (cycle 2): v2 pages own the
                        canonical paths; legacy survives at -legacy
                        for one release as a fallback while we collect
                        signals. Hardcoded refs (/plan, /race-week,
                        /settings) flow to v2 automatically — no
                        ref-site refactor needed. */}
                    <Route index element={<TodayPage />} />
                    <Route path="today" element={<TodayPage />} />
                    <Route path="plan" element={<AthletePlanPage />} />
                    <Route path="workout/build" element={<WorkoutBuilderPage />} />
                    <Route path="record" element={<RecordPage />} />
                    {/* -v2 alias-роуты удалены (W-FIX-B): canonical paths владеют v2-страницами */}
                    <Route path="plan-v2" element={<Navigate to="/plan" replace />} />
                    <Route path="race-week" element={<RaceCenterPage />} />
                    <Route path="race-v2" element={<Navigate to="/race-week" replace />} />
                    {/* /race-results list — orphan по IA_CANON раздел 9: список втянут
                        секцией в /goal/past, detail/compare живут дальше */}
                    <Route path="race-results" element={<Navigate to="/goal/past" replace />} />
                    <Route path="race-results/:resultId" element={<RaceResultDetailPage />} />
                    {/* Был второй адрес того же экрана — `race/:id/recap`. Ни
                        одной ссылки на него: ни из интерфейса, ни из бота, ни
                        из уведомлений. Два адреса одного экрана расходятся в
                        «назад» и в аналитике, поэтому старый адрес не удалён
                        молча, а переадресован — если он где-то остался в
                        закладке, человек попадёт куда шёл. */}
                    <Route
                      path="race/:id/recap"
                      element={<RaceRecapLegacyRedirect />}
                    />
                    <Route path="settings" element={<AthleteSettingsPage />} />
                    <Route path="chat" element={<ChatPage />} />
                    <Route path="analytics" element={<AnalyticsEntry />} />
                    {/* Прямые маршруты минуют AnalyticsEntry, а именно на
                        `/analytics/studio` ведёт связка с «Формы» (studioLink).
                        Провайдер общего курсора нужен и здесь — иначе основной
                        путь атлета оказался бы единственным без него. */}
                    <Route
                      path="analytics/studio"
                      element={<AnalysisCursorProvider><AnalysisStudioPage /></AnalysisCursorProvider>}
                    />
                    <Route
                      path="analytics/classic"
                      element={<AnalysisCursorProvider><AnalyticsPage /></AnalysisCursorProvider>}
                    />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="wellness" element={<WellnessPage />} />
                    <Route path="wellness/checkin" element={<WellnessCheckinPage />} />
                    <Route path="diary" element={<DiaryPage />} />
                    <Route path="questionnaire" element={<QuestionnairePage />} />
                    <Route path="onboarding/connect" element={<OnboardingConnectPage />} />
                    <Route path="onboarding/intro" element={<OnboardingIntroPage />} />
                    <Route path="onboarding/wizard" element={<OnboardingWizardPage />} />
                    {/* P4 aspirational pages — v147 Phase 7 batch */}
                    <Route path="pace-zone-calc" element={<PaceZoneCalcPage />} />
                    <Route path="nutrition-log" element={<NutritionLogPage />} />
                    <Route path="bike-fit" element={<BikeFitPage />} />
                    <Route path="sponsor" element={<SponsorPage />} />
                    <Route path="events/:id/scout" element={<RaceScoutPage />} />
                    <Route path="pregnancy" element={<PregnancyPage />} />
                    <Route path="disability" element={<DisabilityPage />} />
                    <Route path="achievements" element={<AchievementsPage />} />
                    <Route path="zones" element={<ZonesPage />} />
                    <Route path="injuries" element={<InjuryLogPage />} />
                    <Route path="plan/history" element={<PlanHistoryPage />} />
                    <Route path="plan/generate" element={<PlanGeneratePage />} />
                    <Route path="race-results/compare" element={<RaceCompareSplitsPage />} />
                    <Route path="cycle-compare" element={<CycleComparePage />} />
                    <Route path="pr-by-route" element={<PrByRoutePage />} />
                    <Route path="plan/templates" element={<PlanTemplatesPage />} />
                    <Route path="plan/templates/:templateId" element={<PlanTemplateDetailPage />} />
                    <Route path="travel-mode" element={<TravelModePage />} />
                    <Route path="strava-segments" element={<StravaSegmentsPage />} />
                    <Route path="carb-timing" element={<CarbTimingPage />} />
                    <Route path="resilience" element={<ResiliencePage />} />
                    <Route path="ow-spots" element={<OwSpotsPage />} />
                    <Route path="route-heatmap" element={<RouteHeatmapPage />} />
                    <Route path="equipment" element={<EquipmentPage />} />
                    {/* /kb и /kb/:slug — публичные, объявлены выше (C3) */}
                    <Route path="day/:date" element={<DayPage />} />
                    <Route path="workout/:id" element={<WorkoutDetailPage />} />
                    <Route path="goal" element={<GoalInsightsPage />} />
                    <Route path="goal/edit" element={<GoalEditPage />} />
                    <Route path="goal/past" element={<PastRacesPage />} />
                    <Route path="challenges" element={<ChallengesPage />} />
                    <Route path="shoes" element={<ShoesPage />} />
                    <Route path="friends" element={<FriendsPage />} />
                    <Route path="race-teams" element={<RaceTeamsPage />} />
                    <Route path="race-teams/:teamId" element={<RaceTeamsPage />} />
                    <Route path="pair" element={<PairPage />} />
                    <Route path="mentality" element={<MentalityPage />} />
                    <Route path="exercises" element={<ExercisesPage />} />
                    <Route path="exercises/:slug" element={<ExerciseDetailPage />} />
                    <Route path="gear" element={<GearPage />} />
                    <Route path="goal/journey/:id" element={<GoalJourneyPage />} />
                    <Route path="threshold-test" element={<ThresholdTestPage />} />
                    <Route path="sport-profile/:sport" element={<SportProfilePage />} />
                    <Route path="achievements/:code" element={<AchievementDetailPage />} />
                    <Route path="streaks" element={<StreaksPage />} />
                    <Route path="feedback/bug" element={<BugReportPage />} />
                    <Route path="feedback" element={<FeedbackPage />} />
                    <Route path="roadmap" element={<RoadmapPage />} />
                    <Route path="whats-new" element={<WhatsNewPage />} />
                    <Route path="settings/calendar-sync" element={<CalendarSyncPage />} />
                    <Route path="settings/lab" element={<LabPage />} />
                    <Route path="settings/billing" element={<BillingPage />} />
                    <Route path="settings/notifications" element={<NotificationsPage />} />
                    <Route path="settings/language" element={<LanguagePage />} />
                    <Route path="settings/integrations" element={<IntegrationsPage />} />
                    <Route path="settings/sport-metrics" element={<SportMetricsPage />} />
                    <Route path="settings/email" element={<EmailChangePage />} />
                    <Route path="settings/two-factor" element={<TwoFactorPage />} />
                    <Route path="settings/faq" element={<FaqPage />} />
                    <Route path="settings/danger-zone" element={<DangerZonePage />} />
                    <Route path="onboarding/notifications" element={<NotifPermissionPage />} />
                    <Route path="pr/:key" element={<PRDetailPage />} />
                    <Route path="recap/month" element={<MonthInReviewPage />} />
                    <Route path="recap/year/:year" element={<YearInReviewPage />} />
                    {/* W-IA-3 / J-16 — поздняя рефлексия (IA_CANON_v2 раздел 3.5) */}
                    <Route path="reflections" element={<ReflectionsPage />} />
                    {/* W-IA-4 — Спарринг-хаб (IA_CANON_v2 раздел 5) */}
                    <Route path="sparring" element={<SparringHubPage />} />
                    <Route path="privacy-dashboard" element={<PrivacyDashboardPage />} />
                    {/* 2026-07-28: убраны литеральные `wellness/sub` и
                        `equipment/sub`. Компоненты читают useParams().topic,
                        которого у литерального маршрута нет → topic === null,
                        экран не мог отрисоваться ничем. Рабочие адреса —
                        параметрические ниже. */}
                    <Route path="wellness/:topic" element={<WellnessSubPage />} />
                    <Route path="equipment/:topic" element={<EquipmentSubPage />} />
                    <Route path="onboarding/hub" element={<OnboardingHubPage />} />
                    <Route path="events" element={<EventsPage />} />
                    <Route path="events/saved" element={<SavedEventsPage />} />
                    <Route path="events/:id" element={<EventDetailPage />} />
                    <Route path="profile/predictions" element={<MyPredictionsPage />} />
                    <Route path="inbox" element={<InboxPage />} />
                    <Route path="refer" element={<ReferralPage />} />
                    <Route path="path" element={<PathAndGoalPage />} />
                    <Route path="quick-log" element={<QuickLogPage />} />
                    <Route path="crisis/detrain" element={<CrisisDetrainPage />} />
                    <Route path="crisis/illness" element={<CrisisIllnessPage />} />
                    <Route path="search" element={<SearchPage />} />
                    <Route path="more" element={<MorePage />} />
                    <Route path="form" element={<FormPage />} />
                    <Route path="log/strength" element={<StrengthLogPage />} />
                    <Route path="log/swim-drill" element={<SwimDrillPage />} />
                    <Route path="workout/brick" element={<BrickSessionPage />} />
                    <Route path="protocol/altitude" element={<AltitudeProtocolPage />} />
                    <Route path="protocol/heat" element={<HeatAcclimPage />} />
                    <Route path="race/:id/weather" element={<RaceWeatherPage />} />
                    <Route path="lens" element={<LensHubPage />} />
                    <Route path="lens/:slug" element={<LensPage />} />
                    <Route path="race/:id/live" element={<LiveRacePage />} />
                    <Route path="race/:id/pacing" element={<RacePacingPage />} />
                    <Route path="race/:id/fueling" element={<RaceFuelingPage />} />
                    <Route path="race/:id/morning" element={<RaceMorningPage />} />
                    <Route path="workout/:id/live" element={<LiveWorkoutPage />} />
                    <Route path="devices" element={<DevicesPage />} />
                    <Route path="knowledge" element={<KnowledgePage />} />
                    <Route path="knowledge/:slug" element={<KnowledgeArticlePage />} />
                    <Route path="avatar" element={<AvatarPage />} />
                    {/* dev/design kitchen-sink — все DS-примитивы + пульт токенов */}
                    <Route path="ds" element={<DesignGalleryPage />} />
                    {/* Phase 2 merge — /analytics/workstation retired,
                        folded into /analytics as its "Линзы"/"Инструменты"
                        tabs. Redirect keeps old deep-links/bookmarks alive. */}
                    <Route path="analytics/workstation" element={<Navigate to="/analytics" replace />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              </RequireAuth>
            }
          />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
